import { Controller } from "@hotwired/stimulus"

/**
 * Carousel Controller
 * @gem rapid_rails_ui
 *
 * Handles slide rotation, indicator + arrow nav, keyboard nav, auto-rotate
 * with hover/visibility/reduced-motion pauses, loop wraparound, and
 * responsive items-per-view via a CSS variable on the root.
 *
 * Targets: track, slide, indicator, prev, next, liveRegion
 *
 * Values:
 *   autoRotate (Boolean, default false)
 *   interval (Number, default 5000)
 *   loop (Boolean, default true)
 *   pauseOnHover (Boolean, default true)
 *   respectReducedMotion (Boolean, default true) — when false, motion runs
 *     regardless of the OS `prefers-reduced-motion: reduce` setting. Consumer
 *     opt-out; accessibility responsibility shifts to the consumer.
 *   itemsPerView (String, default "1") — JSON-encoded: "1" or {"base":1,"md":3}
 */
export default class extends Controller {
  static targets = ["track", "slide", "indicator", "prev", "next", "liveRegion"]
  static values = {
    autoRotate: { type: Boolean, default: false },
    interval: { type: Number, default: 5000 },
    loop: { type: Boolean, default: true },
    pauseOnHover: { type: Boolean, default: true },
    respectReducedMotion: { type: Boolean, default: true },
    itemsPerView: { type: String, default: "1" }
  }

  connect() {
    this._activeIndex = 0
    this._timer = null
    this._userPaused = false
    this._hoverPaused = false
    this._reducedMotion = this._matchesReducedMotion()
    this._resizeHandler = this._debounce(() => this._resolveItemsPerView(), 100)
    this._visibilityHandler = () => this._onVisibilityChange()

    this._resolveItemsPerView()
    this._setupObserver()
    this._updateActive(0, { announce: false })

    window.addEventListener("resize", this._resizeHandler)
    document.addEventListener("visibilitychange", this._visibilityHandler)

    if (this.autoRotateValue && !this._motionSuppressed()) {
      this._startTimer()
    }
  }

  disconnect() {
    this._stopTimer()
    if (this._observer) {
      this._observer.disconnect()
      this._observer = null
    }
    window.removeEventListener("resize", this._resizeHandler)
    document.removeEventListener("visibilitychange", this._visibilityHandler)
  }

  // ============================================================================
  // PUBLIC ACTIONS
  // ============================================================================

  next() {
    const count = this.slideTargets.length
    if (count === 0) return

    let target = this._activeIndex + 1
    if (target >= count) {
      if (this.loopValue) {
        target = 0
      } else {
        return
      }
    }
    this._scrollTo(target)
  }

  prev() {
    const count = this.slideTargets.length
    if (count === 0) return

    let target = this._activeIndex - 1
    if (target < 0) {
      if (this.loopValue) {
        target = count - 1
      } else {
        return
      }
    }
    this._scrollTo(target)
  }

  goTo(event) {
    const idx = parseInt(event?.params?.index, 10)
    if (Number.isNaN(idx)) return
    this._scrollTo(idx)
  }

  pause() {
    if (!this.pauseOnHoverValue) return
    this._hoverPaused = true
    this._stopTimer()
  }

  resume() {
    if (!this.pauseOnHoverValue) return
    this._hoverPaused = false
    if (this.autoRotateValue && !this._userPaused && !this._motionSuppressed()) {
      this._startTimer()
    }
  }

  onKeydown(event) {
    switch (event.key) {
      case "ArrowRight":
        event.preventDefault()
        this.next()
        break
      case "ArrowLeft":
        event.preventDefault()
        this.prev()
        break
      case "Home":
        event.preventDefault()
        this._scrollTo(0)
        break
      case "End":
        event.preventDefault()
        this._scrollTo(this.slideTargets.length - 1)
        break
    }
  }

  // ============================================================================
  // INTERNALS
  // ============================================================================

  _scrollTo(index) {
    const slide = this.slideTargets[index]
    if (!slide || !this.hasTrackTarget) return

    const track = this.trackTarget
    const slideLeft = slide.offsetLeft - track.offsetLeft
    track.scrollTo({ left: slideLeft, behavior: this._motionSuppressed() ? "auto" : "smooth" })
  }

  _setupObserver() {
    if (!this.hasTrackTarget || typeof IntersectionObserver === "undefined") return

    this._observer = new IntersectionObserver(
      (entries) => {
        // Pick the most-visible entry as the active slide.
        let best = null
        entries.forEach((entry) => {
          if (!best || entry.intersectionRatio > best.intersectionRatio) {
            best = entry
          }
        })
        if (best && best.isIntersecting) {
          const idx = this.slideTargets.indexOf(best.target)
          if (idx >= 0 && idx !== this._activeIndex) {
            this._updateActive(idx)
          }
        }
      },
      {
        root: this.trackTarget,
        threshold: [0.5, 0.6, 0.75]
      }
    )

    this.slideTargets.forEach((slide) => this._observer.observe(slide))
  }

  _updateActive(index, { announce = true } = {}) {
    this._activeIndex = index
    const total = this.slideTargets.length

    // Indicators
    if (this.hasIndicatorTarget) {
      this.indicatorTargets.forEach((dot, i) => {
        dot.setAttribute("aria-current", i === index ? "true" : "false")
      })
    }

    // Slide focus management
    this.slideTargets.forEach((slide, i) => {
      slide.setAttribute("tabindex", i === index ? "0" : "-1")
    })

    // Boundary arrow state
    if (!this.loopValue) {
      if (this.hasPrevTarget) {
        this.prevTargets.forEach((b) => {
          const disabled = index <= 0
          b.disabled = disabled
          b.setAttribute("aria-disabled", disabled ? "true" : "false")
        })
      }
      if (this.hasNextTarget) {
        this.nextTargets.forEach((b) => {
          const disabled = index >= total - 1
          b.disabled = disabled
          b.setAttribute("aria-disabled", disabled ? "true" : "false")
        })
      }
    }

    // Announce
    if (announce && this.hasLiveRegionTarget) {
      this.liveRegionTarget.textContent = `Slide ${index + 1} of ${total}`
    }
  }

  _startTimer() {
    this._stopTimer()
    if (this.intervalValue <= 0) return
    this._timer = window.setInterval(() => {
      this.next()
    }, this.intervalValue)
  }

  _stopTimer() {
    if (this._timer) {
      window.clearInterval(this._timer)
      this._timer = null
    }
  }

  _onVisibilityChange() {
    if (document.visibilityState === "hidden") {
      this._userPaused = true
      this._stopTimer()
    } else {
      this._userPaused = false
      if (this.autoRotateValue && !this._hoverPaused && !this._motionSuppressed()) {
        this._startTimer()
      }
    }
  }

  _matchesReducedMotion() {
    if (typeof window === "undefined" || !window.matchMedia) return false
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  // Single source of truth for "should motion be suppressed right now."
  // Returns true only when the OS prefers reduced motion AND the consumer
  // has not explicitly opted out via respect_reduced_motion: false.
  // Every motion gate in this controller must route through here, otherwise
  // the consumer opt-out becomes inconsistent (the gem-side bug from <=0.50.3).
  _motionSuppressed() {
    return this.respectReducedMotionValue && this._reducedMotion
  }

  _resolveItemsPerView() {
    let raw
    try {
      raw = JSON.parse(this.itemsPerViewValue)
    } catch (_e) {
      raw = 1
    }

    let count
    if (typeof raw === "number") {
      count = raw
    } else if (raw && typeof raw === "object") {
      const w = window.innerWidth
      // Tailwind-ish breakpoints: sm 640, md 768, lg 1024, xl 1280
      const breakpoints = [
        { key: "xl", min: 1280 },
        { key: "lg", min: 1024 },
        { key: "md", min: 768 },
        { key: "sm", min: 640 },
        { key: "base", min: 0 }
      ]
      for (const { key, min } of breakpoints) {
        if (w >= min && raw[key] != null) {
          count = raw[key]
          break
        }
      }
      if (count == null) {
        count = raw.base ?? 1
      }
    } else {
      count = 1
    }

    count = Math.max(1, parseInt(count, 10) || 1)
    const basis = `${100 / count}%`
    this.element.style.setProperty("--carousel-slide-basis", basis)
  }

  _debounce(fn, wait) {
    let t
    return (...args) => {
      window.clearTimeout(t)
      t = window.setTimeout(() => fn.apply(this, args), wait)
    }
  }
}
